/*
/*
(c) Copyright Swisscom (Schweiz) AG. All rights reserved.

 This product is the proprietary and sole property of Swisscom (Schweiz) AG
 Use, duplication or dissemination is subject to prior written consent of
 Swisscom (Schweiz) AG.
*/

import SdxModal from 'sdx/modal/Modal';

import jQuery from 'jquery';

import { lockPageScroll, unlockPageScroll } from 'lib/util/pageScrolling';
import { isImagePath, isExternalUrl } from 'lib/util/url';
import { isMobilePage, ensure } from 'lib/util/helpers';

/**
 * Type for Modal SDX dialog hooks.
 *
 * @author Marcel Greter (TAAGRMAL)
 */

type SDXHandler = (evt? : JQuery.Event) => boolean | void;

/**
 * Config for Modal SDX dialog buttons.
 *
 * @author Marcel Greter (TAAGRMAL)
 * @interface SDXModalDialogButton
 */

export interface SDXModalDialogButton {
	title : string;
	type? : 'close' | 'cancel';
	click? : SDXHandler;
	primary? : boolean;
	thin? : boolean;
}

/**
 * Handlers for Modal SDX dialog events.
 *
 * @author Marcel Greter (TAAGRMAL)
 * @interface SDXModalDialogHandlers
 */

export interface SDXModalDialogHandlers {
	/* Called async if url is given */
	open? : SDXHandler;
	/* Proxy for SDX modal close event */
	close? : SDXHandler;
	/* Proxy for SDX modal cancel event */
	cancel? : SDXHandler;
}

/**
 * Configuration for Modal SDX dialogs.
 * Either pass an url or text (not both).
 *
 * @author Marcel Greter (TAAGRMAL)
 * @interface SDXModalDialogConfig
 */

export interface SDXModalDialogConfig {
	/* Load Portal URL */
	url? : string;
	/* Load URL within iFrame */
	iframe? : boolean,
	/* Optional dialog title */
	title? : string;
	/* Custom inline elements */
	text? : JQuery | string;
	/* Custom block elements */
	html? : JQuery | string;
	/* Custom class on wrapper */
	class? : string;
	/* Defaults to true */
	autoOpen? : boolean;
	/* Defaults to false */
	hideCloser? : boolean;
	/* Defaults to false */
	hideHeader? : boolean;
	/* Event handlers (close/cancel) */
	on? : SDXModalDialogHandlers;
	/* Optional sdx dialog buttons */
	buttons? : SDXModalDialogButton[];
	/* Do not collapse width */
	fillWidth? : boolean,
	/* Contradicts responsive design */
	width? : string | number;
	/* Contradicts responsive design */
	minWidth? : number | string;
	/* Contradicts responsive design */
	height? : string | number;
	/* Contradicts responsive design */
	minHeight? : number | string;
	/* Use notification modal style*/
	notification? : 'general' | 'alert' | 'confirmation';
	/*
	 * As alternative to setting a fixed (min-)width and (min-)height
	 * you can choose to use one of the predefined responsive dimension
	 * settings:
	 *  - prompt - equals to the responsive class settings: col-xs col-md-10 col-lg-8 col-xl-6
	 *  - content - equals to the responsive class settings: col-xs-12 col-lg-10 col-xl-8
	 *
	 * If width, height, min-width, min-height and fillWidth are not sent,
	 * displayMode `content` will be used as the default dimension.
	*/
	displayMode? : DisplayMode;
}

/** Static instance to enforce singleton pattern */
let instance : SDXModalDialog | null = null;

/** Cache already loaded urls to avoid re-fetch */
const contentCache : { [s : string] : string } = {};

// Hardcode common translations inline due to
// Lack of approved JS translation mechanism
const label = {
	close : {
		de: 'Schliessen',
		en: 'Close',
		fr: 'Fermer',
		it: 'Chiudi',
	},
};

// Santize styles of fake containers
// We don't want any margin/paddings
const cleanCss = {
	margin: '0px',
	padding: '0px',
	width: 'auto',
};

// Available display modes with their constant values.
type DisplayMode = 'prompt' | 'content';
const DISPLAY_MODES : Record<DisplayMode, string> = {
	content: 'col-xs-12 col-lg-10 col-xl-8',
	prompt: 'col-xs col-md-10 col-lg-8 col-xl-6',
};

/**
 * Created by Marcel Greter on 21.11.2017.
 *
 * Simple implementation for SDX Modal dialogs. Necessary HTML
 * structure is created once on demand and then re-used. It
 * internally uses/bridges the provided `sdx.Modal` module.
 *
 * @author Marcel Greter (TAAGRMAL)
 * @class SDXModalDialog
 */

class SDXModalDialog {

	/**
	 * Get singleton instance, which will
	 * be created on demand on the first call.
	 */

	public static getInstance() : SDXModalDialog {

		return instance !== null ? instance :
			instance = new SDXModalDialog();

	}

	/** Currently active configuration */
	public config? : SDXModalDialogConfig;
	public $contentHolder! : JQuery;

	/** language determined from HTML.lang */
	private lang : 'de' | 'en' | 'fr' | 'it';
	/** Stores the sdx.Modal dialog instance */
	private dialog : SdxModal | null = null;
	/** Used to add custom styles */
	private $modalWrapper! : JQuery;
	private $wrapper! : JQuery;
	/** Used to append buttons */
	private $buttons! : JQuery;
	/** Used to hide full header */
	private $header! : JQuery;
	/** Used to hide closer */
	private $closer! : JQuery;
	/** Used to hide closer */
	private $outsideHeaderCloser! : JQuery;
	/** Used to set title */
	private $title! : JQuery;
	/** Needed to hide buttons */
	private $body! : JQuery;
	/** Holds the spinner DOM */
	private $spinner! : JQuery;
	/** Used to set content */
	private $content! : JQuery;
	/** Holds the text DOM */
	private $text! : JQuery;
	/** Holds custom html */
	private $html! : JQuery;
	/** Holds main widget */
	private $el! : JQuery;
	/** Stores spinner timeout */
	private spinnerTimer = 0;
	/** Stores callback handlers */
	private on? : SDXModalDialogHandlers;
	/** Store pending request */
	private xhr? : JQueryXHR;
	/* Use notification modal style*/
	private notification? : 'general' | 'alert' | 'confirmation';

	/**
	 * Creates empty instance, call `init`
	 * to create/initialize DOM structure.
	 * @constructor SDXModalDialog
	 */

	private constructor() {
		// Intentionally left blank
		// Initialized lazy on first use
		const lang = jQuery('HTML').attr('lang');
		switch ((lang || 'de').toLocaleLowerCase()) {
			case 'en': this.lang = 'en'; break;
			case 'fr': this.lang = 'fr'; break;
			case 'it': this.lang = 'it'; break;
			default: this.lang = 'de'; break;
		}
		// Just here to satisfy TypeScript and TSLint
		this.$wrapper = this.$buttons = this.$header = this.$closer =
		this.$title = this.$body = this.$spinner = this.$content =
		this.$text = this.$html = this.$el = jQuery('<div>');
	}

	/**
	 * Initialize and show modal dialog with given config.
	 */

	// tslint:disable:cyclomatic-complexity
	public show(config : SDXModalDialogConfig) : void {

		this.on = config.on;
		this.config = config;

		this.init();
		this.reset(config);

		if(config.notification) {
			// Render as a notification modal (different layout as regular modal.)
			// See https://sdx.swisscom.ch/components_-_notifications_(modal).html
			this.initContentAsNotificationModal(config);

			// Notification modals have predefined dimensions.
			// They can not be overriden by the config.

		} else {
			// Render as a regular modal
			// See https://sdx.swisscom.ch/components_-_modals.html
			this.setTitle(config.title);

			if(config.html || config.text) {
				this.setSimpleText(config.text);
				this.setHTML(config.html);
			} else if(isImagePath(config.url)) {
				// Show scaled image with an image tag
				const img = jQuery('<img/>', {src: config.url, alt: ''});
				img.css({ height: 'auto', width: '100%' });
				// Show html code in modal
				this.setHTML(img);
			} else if(config.iframe || isExternalUrl(config.url)) {
				this.setIframeContent(config);
			} else if(config.url) {
				this.setContentFromInternalUrl(config);
			}

			// Set Dialog Dimensions.
			this.setDimensions(config);
		}

		// Add configured buttons.
		this.addButtons(config);

		if (config.autoOpen !== false) {
			this.open();
		}

		ensure(this.$body, false);

	}

	/**
	 * Open modal dialog with current config.
	 * Not very useful, but here for completeness,
	 * in case you want to re-open after close.
	 * Or if you have set `autoOpen` to `false`
	 */

	public open() : void {

		this.init();
		lockPageScroll();
		// Invoke sdx method
		this.dialog!.open();

		document.body.classList.add('js-modal-open');

		// Dispatch open event (legacy callback)
		// Will be called once url is loaded (async)
		if (this.on && typeof this.on.open === 'function') {
			this.on.open.call(this);
		}

	}

	/**
	 * Close currently shown dialog.
	 * Will emit sdx dialog events.
	 */

	public close() : void {

		this.init();
		unlockPageScroll();
		// Invoke sdx method
		this.dialog!.close();

		document.body.classList.remove('js-modal-open');

	}

	/**
	 * Render the dialog as a notification modal.
	 *
	 * @param config {SDXModalDialogConfig} - the dialog config to use to render the dialog
	 */
	private initContentAsNotificationModal(config : SDXModalDialogConfig) : void {
		if(!config.notification) {
			return;
		}
		this.notification = config.notification;

		this.$header.toggle(false);
		this.$outsideHeaderCloser.toggle(true);
		this.$body.empty();

		const text = config.text || '';
		let icon = '';
		let notificationClass = '';
		switch (config.notification) {
			case 'general': {
				icon = 'icon-051-message';
				break;
			}
			case 'alert': {
				icon = 'icon-026-exclamation-mark-circle';
				notificationClass = 'modal--notification--alert';
				break;
			}
			case 'confirmation': {
				icon = 'icon-012-check-mark-circle';
				notificationClass = 'modal--notification--confirmation';
				break;
			}
			default: break;
		}
		const msg = `<div class="notification-message">
				<i class="icon ${icon}" aria-hidden="true"></i>
				${String(text)}
			</div>`;

		this.setHTML(msg);

		this.$modalWrapper.addClass(`modal--notification ${notificationClass}`);
	}

	/**
	 * Initialize SDX dialog DOM structure.
	 * @returns sdx.Modal instance
	 */

	private initDialogSDX() : SdxModal {

		const modalId = 'm-' + Date.now().toString();

		const $container = jQuery('<DIV class="container sdx-modal-holder">');
		const $sdxContainer = jQuery('<DIV class="sdx-container">');
		const $modalWrapper = jQuery(`<DIV class="modal" role="dialog" aria-labelledby="${modalId}" tabindex="-1">`);
		const $modalContent = jQuery('<DIV class="modal__content">');
		const $modalHeader = jQuery('<DIV class="modal__header">');
		const $modalBody = jQuery('<DIV class="modal__body">');
		const $closeIcon = jQuery('<I class="icon icon-022-close" aria-hidden="true">');
		const $closer = jQuery('<BUTTON class="modal__close modal-cancel">');
		const $outsideHeaderCloser = jQuery('<BUTTON class="modal__close modal-cancel">');
		const $buttons = jQuery('<DIV class="button-group">');
		const $content = jQuery('<DIV>');
		const $title = jQuery(`<P class="sc-gray text-h4 dialog-title" role="heading" aria-level="1" id="${modalId}">`);
		const $html = jQuery('<DIV>');
		const $text = jQuery('<P>');

		this.$contentHolder = $modalBody;

		// Create sdx html structure
		$modalWrapper.appendTo($sdxContainer);
		$container.appendTo($modalWrapper);
		$modalContent.appendTo($container);
		$modalHeader.appendTo($modalContent);
		$outsideHeaderCloser.appendTo($modalContent);
		$modalBody.appendTo($modalContent);
		$title.appendTo($modalHeader);
		$closer.appendTo($modalHeader);
		$closeIcon.appendTo([$closer[0], $outsideHeaderCloser[0]]);

		// ToDo: make this configurable?
		$buttons.addClass('button-group--responsive');
		$closer.attr('aria-label', label.close[this.lang]);

		// Sanitize a few styles
		$content.css(cleanCss);
		$content.css('float', 'none');
		$buttons.css('clear', 'both');

		this.$modalWrapper = $modalWrapper;
		this.$wrapper = $modalContent;
		this.$buttons = $buttons;
		this.$header = $modalHeader;
		this.$closer = $closer;
		this.$outsideHeaderCloser = $outsideHeaderCloser;
		this.$title = $title;
		this.$body = $modalBody;
		this.$content = $content;
		this.$text = $text;
		this.$html = $html;

		this.$el = $sdxContainer;

		jQuery($modalWrapper[0]).on('cancelled', event => {
			unlockPageScroll();
			document.body.classList.remove('js-modal-open');
			if (this.on && typeof this.on.cancel === 'function') {
				return this.on.cancel.call(this, event);
			}
		});

		jQuery($modalWrapper[0]).on('closed', event => {
			unlockPageScroll();
			document.body.classList.remove('js-modal-open');
			if (this.on && typeof this.on.close === 'function') {
				return this.on.close.call(this, event);
			}
		});

		// Create loading spinner dom
		this.$spinner = jQuery('<DIV>')
			.css('margin', 'auto')
			.addClass('loader-spinner')
			.addClass('loader-spinner--large')
			.addClass('loader-spinner--plain');

		// Instantiate a new sdx modal dialog
		return new SdxModal($modalWrapper[0]);

	}

	/**
	 * Ensures SDX modal dialog is initialized.
	 * Multiple calls do not have any effect.
	 */

	private init() : void {

		// Create HTML singleton
		if (this.dialog === null) {
			// Create sdx dialog structure
			this.dialog = this.initDialogSDX();
			jQuery('BODY').append(this.$el);
			// Add gray-out background to sdx-container
			(this.dialog as any)._backdropParent = this.$el[0];
		}
	}

	/**
	 * Helper to create and set SDX dialog buttons DOM.
	 */

	private addButtons(config : SDXModalDialogConfig) : void {

		if (!config.buttons || !config.buttons.length) {
			return;
		}

		const buttons : SDXModalDialogButton[] = config.buttons;

		if (this.notification) {
			this.$buttons.removeClass('button-group button-group--responsive');
		}

		for (const button of buttons) {

			const $button = jQuery('<BUTTON>')
				.addClass('button')
				.addClass('button--responsive')
				.append(button.title)
				.appendTo(this.$buttons);

			if (button.type) {
				const type = button.type;
				$button.addClass('modal-' + type);
			}

			if (button.primary) {
				$button.addClass('button--primary');
			} else {
				$button.addClass('button--secondary');
			}

			if (button.thin) {
				$button.removeClass('button--primary button--secondary');
				$button.addClass('button--thin');
			}

			if (button.click) {
				// Call in the context of modal dialog
				$button.click(button.click.bind(this));
			}
		}


		this.$buttons.appendTo(this.$body);
	}

	/**
	 * Resetting the content of the modal.
	 * @param config	{SDXModalDialogConfig}	The config to use for resetting the modal.
	 */
	private reset(config : SDXModalDialogConfig) : void {

		this.$content.empty().detach();
		this.$html.empty().hide(0).detach();
		this.$text.empty().hide(0).detach();
		this.$title.empty();
		// Hiding buttons is not good enough as we have a list-child
		// Selector on the sdx styles to remove the bottom margin
		this.$buttons.empty().detach();

		this.$modalWrapper.removeClass('modal--notification modal--notification--confirmation modal--notification--alert');

		this.$closer.toggle(!config.hideCloser);
		this.$header.toggle(!config.hideHeader);
		this.$outsideHeaderCloser.toggle(!config.hideCloser);

		if (!config.hideHeader) {
			this.$outsideHeaderCloser.toggle(false);
		}

		this.$wrapper.attr('class', 'modal__content');
		this.$wrapper.addClass(config.class || '');
		// Remove all previously set styles for width and height.
		this.$wrapper.attr('style', null);
	}

	/**
	 * Load and cache content from a internal url
	 * @param config {SDXModalDialogConfig} - the dialog congfig
	 */
	private setContentFromInternalUrl(config : SDXModalDialogConfig) : void {
		if(!config.url || isExternalUrl(config.url)) {
			return;
		}

		let url = config.url;
		if (isMobilePage()) {
			// Add mobile page selector if we are embedded in a mobile page
			url = url.replace(/(?:\.mobile)?\.html([#?.]|$)/, '.mobile.html$1');
		}

		if (url in contentCache) {
			const content = contentCache[url];
			this.showContent(config, content);

			return;
		}

		// Abort any request in progress
		if (this.xhr) { this.xhr.abort(); }
		// Fetch url (store pending request)
		this.xhr = jQuery.get(url, content => {
			this.xhr = undefined;
			contentCache[url] = content;
			this.showContent(config, content);
		});
		// Clear pending timer
		if (this.spinnerTimer) {
			clearTimeout(this.spinnerTimer);
		}
		// Show spinner if load takes more than 1s
		this.spinnerTimer = setTimeout(() => {
			this.$content.append(this.$spinner);
		}, 1000);
	}

	/**
	 * Set the dimensions of the dialog.
	 * @param config {SDXModalDialogConfig} - the dialog congfig
	 */
	private setDimensions(config : SDXModalDialogConfig) : void {
		if (config.minWidth) {
			let minWidth = config.minWidth;
			// .css('min-width', '500') does not work!
			if (!Number.isNaN(Number(minWidth))) {
				minWidth = minWidth + 'px';
			}
			this.$wrapper.css('min-width', minWidth);
		}

		if (config.minHeight) {
			let minHeight = config.minHeight;
			// .css('min-height', '500') does not work!
			if (!Number.isNaN(Number(minHeight))) {
				minHeight = minHeight + 'px';
			}
			this.$wrapper.css('min-height', minHeight);
		}

		if (config.height) {
			this.$wrapper.css('height', String(config.height));
		}

		if(config.displayMode) {
			this.$wrapper.addClass(DISPLAY_MODES[config.displayMode]);
		} else if (config.fillWidth) {
			// Do not collapse small content
			this.$wrapper.css('width', '100%');
		} else if (config.width) {
			// Use specific width otherwise
			this.$wrapper.css('width', String(config.width));
		} else {
			this.$wrapper.addClass(DISPLAY_MODES.content);
		}
	}

	/**
	 * Set content as an iFrame
	 * @param config {SDXModalDialogConfig} - the dialog congfig
	 */
	private setIframeContent(config : SDXModalDialogConfig) : void {
		if(!config.url) {
			return;
		}

		const dims = {
			height : '100%',
			width : '100%',
		};

		this.$content.css(dims);

		const $element = jQuery('<IFRAME>')
			.css('margin', '0px')
			.css('border', 'none')
			.attr('src', config.url).attr(dims);

		this.appendToBody($element);
	}

	/**
	 * Set an html string as the new modal content.
	 * Maybe used in combination with simple text.
	 * @param html {string | JQuery} - The new html content
	 */
	private setHTML(html? : string | JQuery) : void {
		if(!html) {
			return;
		}

		this.$html.appendTo(this.$body);
		this.$html.append(html);
		this.$html.show(0);
	}

	/**
	 * Set a new text string as the modal content.
	 * Maybe used in combination with html text.
	 * @param text {string | JQuery} - The new text content
	 */
	private setSimpleText(text? : string | JQuery) : void {
		if(!text) {
			return;
		}

		this.$text.appendTo(this.$body);
		this.$text.append(text);
		this.$text.show(0);
	}

	/**
	 * Set a new modal title.
	 * @param title {string} - The new modal title
	 */
	private setTitle(title? : string) : void {
		if(!title) {
			return;
		}
		this.$title.text(title);
	}

	/**
	 * Helper to create and set SDX dialog content DOM.
	 */
	private showContent(config : any, content : string) : void {

		this.$content.empty();
		clearTimeout(this.spinnerTimer);
		const parser = new DOMParser();
		const doc = parser.parseFromString(content, 'text/html');

		// Finding a title for the modal.
		let title = config.title;
		if(!title) {
			const titleElement = doc.querySelector('.dialog-title');
			if(!titleElement) {
				title = doc.title.substring(0, doc.title.lastIndexOf('|'));
			} else {
				title = titleElement.textContent;
			}
		}

		this.setTitle(title);

		// Finding the content.
		const portalContentPageSelector = isMobilePage() ? '.stageMobile:first-child > .parsys' : '.content > DIV.parsys';
		const contentSelector = config.contentSelector || portalContentPageSelector;
		let newContent = doc.querySelectorAll<HTMLElement>(contentSelector);

		if(newContent.length) {
			const $newContent = $(Array.prototype.slice.call(newContent));
			this.appendToBody($newContent, true);

			return;
		}

		// Maybe we loaded a sdx-template based page
		const sdxTemplateSelector = '.main-wrapper > .page';
		newContent = doc.querySelectorAll<HTMLElement>(sdxTemplateSelector);
		const $pageContent = $(newContent[0]);
		$pageContent.removeClass('container-fluid');
		this.appendToBody($pageContent);
	}

	/**
	 * Attach and show the content.
	 */
	private appendToBody($content : JQuery, shouldWrap = false) : void {

		// Needed for IE11 (DAC-16797)
		for (let i = 0; i < $content.length; i++) {
			// OwnerDocument seems to be the DOMParser!?
			$content[i] = document.adoptNode($content[i]);
		}

		if(shouldWrap) {
			$content = $('<div class="content fake"></div>')
						.css(cleanCss)
						.css('float', 'none')
						.append($content);
			$content = $('<div class="middle fake"></div>')
						.css(cleanCss)
						.append($content);
		}

		this.$content.append($content);
		/*
			* The content has to be the first item in the body.
			* Since the content may have been ajax loaded, the configured buttons
			* may have been attached to the body already.
			* Hence, prepend the content to the body to avoid that the buttons appear
			* before the content.
			*/
		this.$content.prependTo(this.$body);
		this.$content.show(0);

		ensure($content, false);
	}
}

// Export the singleton instance directly
export default SDXModalDialog.getInstance();



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/generic/SDXModalDialog.ts